import MissingLineItemVariantError from "../../errors/MissingLineItemVariantError";
import { requireConfigValue } from "../../isomorphic-config";
import { jsonApi } from "@spree/storefront-api-v2-sdk";
import createGetAbsoluteImageUrl from "../create-get-absolute-image-url";
import getMediaGallery from "../get-media-gallery";
import normalizeProduct from "./normalize-product";
import expandOptions from "../expand-options";
const placeholderImage = requireConfigValue("lineItemPlaceholderImageUrl");
const isColorProductOption = (productOptionType)=>{
    return productOptionType.attributes.presentation === "Color";
};
const normalizeVariant = (spreeSuccessResponse, spreeVariant)=>{
    var ref, ref1;
    const spreeProduct = jsonApi.findSingleRelationshipDocument(spreeSuccessResponse, spreeVariant, "product");
    if (spreeProduct === null) {
        throw new MissingLineItemVariantError(`Couldn't find product for variant with id ${spreeVariant.id}.`);
    }
    const spreeVariantImageRecords = jsonApi.findRelationshipDocuments(spreeSuccessResponse, spreeVariant, "images");
    let lineItemImage;
    const variantImage = getMediaGallery(spreeVariantImageRecords, createGetAbsoluteImageUrl(requireConfigValue("imageHost")))[0];
    if (variantImage) {
        lineItemImage = variantImage;
    } else {
        const spreeProductImageRecords = jsonApi.findRelationshipDocuments(spreeSuccessResponse, spreeProduct, "images");
        const productImage = getMediaGallery(spreeProductImageRecords, createGetAbsoluteImageUrl(requireConfigValue("imageHost")))[0];
        lineItemImage = productImage;
    }
    const image = lineItemImage ?? (placeholderImage === false ? undefined : {
        url: placeholderImage
    });
    let variantOptions = [];
    const spreeOptionValues = jsonApi.findRelationshipDocuments(spreeSuccessResponse, spreeVariant, "option_values");
    spreeOptionValues.forEach((spreeOptionValue)=>{
        variantOptions = expandOptions(spreeSuccessResponse, spreeOptionValue, variantOptions);
    });
    // If variant has a 'metal' option value, add it to the slug.
    const metalOption = (ref1 = (ref = variantOptions.find((opt)=>opt.displayName.toLowerCase() === "metal")) == null ? void 0 : ref.values[0]) == null ? void 0 : ref1.label.toLowerCase();
    // E.g.: If variant has 'White Gold 14K' metal option value, the slug should be '/[product-slug]-white-gold-14k'.
    // Otherwise return the product slug only.
    const variantSlug = metalOption ? `${spreeProduct.attributes.slug}-${metalOption.replace(/\s/g, "-")}` : spreeProduct.attributes.slug;
    return {
        id: spreeVariant.id,
        sku: spreeVariant.attributes.sku,
        name: spreeProduct.attributes.name,
        slug: variantSlug,
        description: spreeProduct.attributes.description,
        requiresShipping: true,
        price: parseFloat(spreeVariant.attributes.price),
        listPrice: parseFloat(spreeVariant.attributes.price),
        image,
        isInStock: spreeVariant.attributes.in_stock,
        availableForSale: spreeVariant.attributes.available,
        ...spreeVariant.attributes.weight === "0.0" ? {} : {
            weight: {
                value: parseFloat(spreeVariant.attributes.weight),
                unit: "KILOGRAMS"
            }
        }
    };
};
const normalizeLineItem = (spreeSuccessResponse, spreeLineItem)=>{
    const variant = jsonApi.findSingleRelationshipDocument(spreeSuccessResponse, spreeLineItem, "variant");
    if (variant === null) {
        throw new MissingLineItemVariantError(`Couldn't find variant for line item with id ${spreeLineItem.id}.`);
    }
    const product = jsonApi.findSingleRelationshipDocument(spreeSuccessResponse, variant, "product");
    if (product === null) {
        throw new MissingLineItemVariantError(`Couldn't find product for variant with id ${variant.id}.`);
    }
    // CartItem.tsx expects path without a '/' prefix unlike pages/product/[slug].tsx and others.
    const path = `${product.attributes.slug}`;
    const spreeOptionValues = jsonApi.findRelationshipDocuments(spreeSuccessResponse, variant, "option_values");
    const options = spreeOptionValues.map((spreeOptionValue)=>{
        const spreeOptionType = jsonApi.findSingleRelationshipDocument(spreeSuccessResponse, spreeOptionValue, "option_type");
        if (spreeOptionType === null) {
            throw new MissingLineItemVariantError(`Couldn't find option type of option value with id ${spreeOptionValue.id}.`);
        }
        const label = isColorProductOption(spreeOptionType) ? spreeOptionValue.attributes.name : spreeOptionValue.attributes.presentation;
        return {
            id: spreeOptionValue.id,
            name: spreeOptionType.attributes.presentation,
            value: label
        };
    });
    let diamond;
    try {
        const spreeYadavDiamond = jsonApi.findSingleRelationshipDocument(spreeSuccessResponse, product, "diamond");
        if (spreeYadavDiamond) {
            diamond = spreeYadavDiamond.attributes;
        } else {
            // This should never happen but we set 'null' just in case no data received from the API.
            diamond = null;
        }
    } catch (error) {
        // The product has no diamond relationship.
        diamond = null;
    }
    return {
        id: spreeLineItem.id,
        variantId: variant.id,
        productId: product.id,
        name: spreeLineItem.attributes.name,
        quantity: spreeLineItem.attributes.quantity,
        discounts: [],
        path,
        variant: normalizeVariant(spreeSuccessResponse, variant),
        options,
        price: spreeLineItem.attributes.price,
        size: spreeLineItem.attributes.size,
        productType: product.attributes.product_type,
        ringSizes: product.attributes.ring_sizes,
        diamond,
        groupId: spreeLineItem.attributes.group_id,
        product: normalizeProduct(spreeSuccessResponse, product)
    };
};
const normalizeCart = (spreeSuccessResponse, spreeCart)=>{
    var ref;
    const lineItems = jsonApi.findRelationshipDocuments(spreeSuccessResponse, spreeCart, "line_items").map((lineItem)=>normalizeLineItem(spreeSuccessResponse, lineItem));
    const promotions = jsonApi.findRelationshipDocuments(spreeSuccessResponse, spreeCart, "promotions");
    let billingAddress;
    let shippingAddress;
    let payments;
    let shipments;
    // Billing Address
    try {
        const spreeOrderBillingAddress = jsonApi.findSingleRelationshipDocument(spreeSuccessResponse, spreeCart, "billing_address");
        if (spreeOrderBillingAddress) {
            billingAddress = {
                firstName: spreeOrderBillingAddress.attributes.firstname,
                lastName: spreeOrderBillingAddress.attributes.lastname,
                address1: spreeOrderBillingAddress.attributes.address1,
                address2: spreeOrderBillingAddress.attributes.address2,
                city: spreeOrderBillingAddress.attributes.city,
                zipcode: spreeOrderBillingAddress.attributes.zipcode,
                phone: spreeOrderBillingAddress.attributes.phone,
                stateName: spreeOrderBillingAddress.attributes.state_name,
                countryName: spreeOrderBillingAddress.attributes.country_name,
                countryIso3: spreeOrderBillingAddress.attributes.country_iso3,
                countryIso: spreeOrderBillingAddress.attributes.country_iso,
                stateCode: spreeOrderBillingAddress.attributes.state_code
            };
        } else {
            billingAddress = null;
        }
    } catch (error) {
        billingAddress = null;
    }
    // Shipping Address
    try {
        const spreeOrderShippingAddress = jsonApi.findSingleRelationshipDocument(spreeSuccessResponse, spreeCart, "shipping_address");
        if (spreeOrderShippingAddress) {
            shippingAddress = {
                firstName: spreeOrderShippingAddress.attributes.firstname,
                lastName: spreeOrderShippingAddress.attributes.lastname,
                address1: spreeOrderShippingAddress.attributes.address1,
                address2: spreeOrderShippingAddress.attributes.address2,
                city: spreeOrderShippingAddress.attributes.city,
                zipcode: spreeOrderShippingAddress.attributes.zipcode,
                phone: spreeOrderShippingAddress.attributes.phone,
                stateName: spreeOrderShippingAddress.attributes.state_name,
                countryName: spreeOrderShippingAddress.attributes.country_name,
                countryIso3: spreeOrderShippingAddress.attributes.country_iso3,
                countryIso: spreeOrderShippingAddress.attributes.country_iso,
                stateCode: spreeOrderShippingAddress.attributes.state_code
            };
        } else {
            shippingAddress = null;
        }
    } catch (error1) {
        shippingAddress = null;
    }
    // Payments
    try {
        const spreeOrderPayments = jsonApi.findRelationshipDocuments(spreeSuccessResponse, spreeCart, "payments");
        if (spreeOrderPayments && spreeOrderPayments.length > 0) {
            payments = spreeOrderPayments.map((spreeOrderPayment)=>{
                return {
                    amount: spreeOrderPayment.attributes.amount,
                    paymentMethodName: spreeOrderPayment.attributes.payment_method_name,
                    state: spreeOrderPayment.attributes.state
                };
            });
        } else {
            payments = null;
        }
    } catch (error2) {
        payments = null;
    }
    // Shipments
    try {
        const spreeOrderShipments = jsonApi.findRelationshipDocuments(spreeSuccessResponse, spreeCart, "shipments");
        if (spreeOrderShipments && spreeOrderShipments.length > 0) {
            shipments = spreeOrderShipments.map((spreeOrderShipment)=>{
                // Selected shipping rate
                let selectedShippingRate;
                try {
                    const spreeSelectedShippingRate = jsonApi.findSingleRelationshipDocument(spreeSuccessResponse, spreeOrderShipment, "selected_shipping_rate");
                    if (spreeSelectedShippingRate) {
                        selectedShippingRate = {
                            name: spreeSelectedShippingRate.attributes.name,
                            selected: spreeSelectedShippingRate.attributes.selected,
                            finalPrice: spreeSelectedShippingRate.attributes.final_price,
                            displayFinalPrice: spreeSelectedShippingRate.attributes.display_final_price,
                            cost: spreeSelectedShippingRate.attributes.cost,
                            displayCost: spreeSelectedShippingRate.attributes.display_cost,
                            taxAmount: spreeSelectedShippingRate.attributes.tax_amount,
                            displayTaxAmount: spreeSelectedShippingRate.attributes.display_tax_amount,
                            free: spreeSelectedShippingRate.attributes.free
                        };
                    } else {
                        selectedShippingRate = null;
                    }
                } catch (error) {
                    selectedShippingRate = null;
                }
                return {
                    number: spreeOrderShipment.attributes.number,
                    finalPrice: spreeOrderShipment.attributes.final_price,
                    displayFinalPrice: spreeOrderShipment.attributes.display_final_price,
                    state: spreeOrderShipment.attributes.state,
                    shippedAt: spreeOrderShipment.attributes.shipped_at,
                    trackingUrl: spreeOrderShipment.attributes.tracking_url,
                    free: spreeOrderShipment.attributes.free,
                    selectedShippingRate: selectedShippingRate
                };
            });
        } else {
            shipments = null;
        }
    } catch (error3) {
        shipments = null;
    }
    return {
        id: spreeCart.id,
        createdAt: spreeCart.attributes.created_at.toString(),
        currency: {
            code: spreeCart.attributes.currency
        },
        taxesIncluded: false,
        taxTotal: spreeCart.attributes.tax_total,
        taxDisplay: spreeCart.attributes.display_tax_total,
        lineItems: lineItems,
        lineItemsSubtotalPrice: parseFloat(spreeCart.attributes.item_total),
        subtotalPrice: parseFloat(spreeCart.attributes.item_total),
        totalPrice: parseFloat(spreeCart.attributes.total),
        customerId: spreeCart.attributes.token,
        email: spreeCart.attributes.email,
        discounts: [],
        displayPromoTotal: spreeCart.attributes.display_promo_total,
        promotions: promotions,
        billingAddress: billingAddress,
        shippingAddress: shippingAddress,
        payments: payments,
        shipments: shipments,
        promoTotal: spreeCart.attributes.promo_total,
        adjustmentTotal: spreeCart.attributes.adjustment_total,
        orderNumber: spreeCart.attributes.number,
        completedAt: ((ref = spreeCart.attributes.completed_at) == null ? void 0 : ref.toString()) || null,
        taxRates: spreeCart.attributes.tax_rates || null
    };
};
export { normalizeLineItem };
export default normalizeCart;
